import { useEffect, useRef, useState } from 'react';
import { loadYouTubeIframeApi } from '../platformPlayerLoaders';

// Drives a single YouTube IFrame Player instance bound to the returned
// containerRef. Three call sites (artists_table.jsx's FallbackTrackPlayer,
// MobileArtistsTable.jsx's MobileFallbackPlayer, ArtistPage.jsx's
// ArtistListenPlayer) each hand-rolled this same player-creation/teardown
// logic, which is how the "pause doesn't update the UI" bug (fixed
// by making onStateChange, not the click handler, the single source of
// truth for state) had to be fixed three times over. Callers layer their own
// concerns - exclusive-playback coordination, position polling, an outer
// state machine that also covers non-YouTube tiers - on top by watching the
// returned `state`.
//
// state: 'idle' (no videoId yet) | 'ready' | 'playing' | 'none' (error)
export function useYoutubeFallbackPlayer(videoId, { autoplay = true } = {}) {
  const [state, setState] = useState('idle');
  const containerRef = useRef(null);
  const playerRef = useRef(null);

  useEffect(() => {
    setState('idle');
    if (!videoId) return;
    let cancelled = false;

    loadYouTubeIframeApi().then((YT) => {
      if (cancelled || !containerRef.current) return;
      const player = new YT.Player(containerRef.current, {
        // The IFrame API replaces the target element with its own <iframe>
        // rather than rendering inside it - any CSS on the target div itself
        // is discarded along with the div, so explicit width/height here is
        // what actually sizes the resulting iframe; positioning has to live
        // one level up (see YoutubeMiniPlayer).
        width: '200', height: '200',
        videoId,
        playerVars: { autoplay: autoplay ? 1 : 0 },
        events: {
          onReady: () => {
            // Baseline in case autoplay is blocked/delayed - PLAYING (below)
            // upgrades this the moment real playback actually starts.
            if (!cancelled) setState('ready');
          },
          onStateChange: (event) => {
            if (cancelled) return;
            if (event.data === window.YT.PlayerState.PLAYING) {
              setState('playing');
            } else if (event.data === window.YT.PlayerState.PAUSED || event.data === window.YT.PlayerState.ENDED) {
              setState('ready');
            }
          },
          // A video that's age-restricted, region-blocked, or has embedding
          // disabled fires onError instead of ever reaching onStateChange.
          onError: () => {
            if (!cancelled) setState('none');
          },
        },
      });
      playerRef.current = player;
    });

    return () => {
      cancelled = true;
      if (playerRef.current?.destroy) playerRef.current.destroy();
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  const play = () => { if (playerRef.current?.playVideo) playerRef.current.playVideo(); };
  const pause = () => { if (playerRef.current?.pauseVideo) playerRef.current.pauseVideo(); };
  const getCurrentTime = () => (playerRef.current?.getCurrentTime ? playerRef.current.getCurrentTime() : 0);
  const getDuration = () => (playerRef.current?.getDuration ? playerRef.current.getDuration() : 0);

  return { containerRef, state, play, pause, getCurrentTime, getDuration };
}

export default useYoutubeFallbackPlayer;
